import { Colors, IAccountStatusItem, ObjectAnyType, WidgetConfig, WidgetLayout, WidgetList } from '@/types/dashboard'
import {
    getWidgetParameterConfig,
    isAreaChartWidget,
    isMultiQueuesDashboard,
    isQueueChart,
    isQueueGauge,
    isRealtimeWidget,
    isWidgetCard
} from '@/helpers/widgetUtils'
import {
    defaultAreaChartColors,
    defaultMultiQueuesDashboardColors,
    defaultQueueChartColors,
    defaultQueueGaugeChartColors,
    getAllDefaultCardColors,
    realTimeSettings
} from '@/enum/defaultWidgetSettings'
import store from '@/store/store'
import cloneDeep from 'lodash/cloneDeep'
import i18n from '@/i18n'
import ENUM from '@/enum/parameters'
import { IWidgetTemplateListItem } from '@/types/templates'
import { getWidgetTemplate } from '@/composables/globalData/useTemplatesCategoryData'
import { getSelectedAccountStatusTextById, selectedAccountData } from '@/composables/globalData/useEntitiesData'
import { MAXIMUM_RANGE_DEFAULT } from '@/components/RedesignComponents/Widgets/widgets.enum'
import redesignStatusTypes, { callStatuses, otherStatuses, redesignStatusTypesWithCounterWidgets, Statuses } from '@/enum/redesignStatusTypes'
import { WidgetPredefineData } from '@/enum/widgetPredefineData'
import { getAllSeries } from '@/enum/queueConfigs'

export function getStatusTypes () {
    return redesignStatusTypes.value || {}
}

export function cloneWidgetModel (widgetOriginal: WidgetList) {
    const widget = cloneDeep(widgetOriginal)
    // console.log({ DataTypeID: widget.DataTypeID })
    if (WidgetPredefineData[widget.DataTypeID]) {
        WidgetPredefineData[widget.DataTypeID](widget)
    }
    const widgetColors = widget.WidgetLayout.colors ?? {}
    const widgetLayout = {
        ...widget.WidgetLayout,
        colors: { ...widgetColors }
    }


    if (isMultiQueuesDashboard(widget)) {
        widgetLayout.backQueueSLASound = widgetLayout.backQueueSLASound || 'none'
        widgetLayout.maximumWarningCallersValue = widgetLayout.maximumWarningCallersValue || 0
        widgetLayout.maximumLimitCallersValue = widgetLayout.maximumLimitCallersValue || 0
        widgetLayout.maximumWarningMaxRingTimeValue = widgetLayout.maximumWarningMaxRingTimeValue || 0
        widgetLayout.maximumLimitMaxRingTimeValue = widgetLayout.maximumLimitMaxRingTimeValue || 0
        widgetLayout.maximumWarningCallersColor = widgetLayout.maximumWarningCallersColor || ''
        widgetLayout.maximumLimitCallersColor = widgetLayout.maximumLimitCallersColor || ''
        widgetLayout.maximumWarningMaxRingTimeColor = widgetLayout.maximumWarningMaxRingTimeColor || ''
        widgetLayout.maximumLimitMaxRingTimeColor = widgetLayout.maximumLimitMaxRingTimeColor || ''
        widgetLayout.maximumWarningCallersSound = widgetLayout.maximumWarningCallersSound || 'none'
        widgetLayout.maximumLimitCallersSound = widgetLayout.maximumLimitCallersSound || 'none'
        widgetLayout.maximumWarningMaxRingTimeSound = widgetLayout.maximumWarningMaxRingTimeSound || 'none'
        widgetLayout.maximumLimitMaxRingTimeSound = widgetLayout.maximumLimitMaxRingTimeSound || 'none'
        widgetLayout.displayRowWithTotals = widgetLayout.displayRowWithTotals ?? true
    }

    if (isRealtimeWidget(widget) && !widget.WidgetLayout.settings) {
        widgetLayout.settings = { ...realTimeSettings }
    }

    if (isQueueChart(widget)) {
        if (!widget.WidgetLayout.showQueues) {
            const queues = store.state?.queues.all || []
            widgetLayout.showQueues = queues.map((el: ObjectAnyType) => el.QueueID)
            widgetLayout.showSeries = getAllSeries(selectedAccountData.value?.AccountBreaks).map(item => item.value).filter(item => item !== Statuses.LOGOUT)
        }
    }
    if (isQueueGauge(widget)) {
        if (!widget.WidgetLayout.maximumRange) {
            widgetLayout.maximumRange = MAXIMUM_RANGE_DEFAULT
        }
    }

    if (!widgetLayout.TemplateSettings) {
        widgetLayout.TemplateSettings = fillTemplateSettings(widget)
    }
    widget.WidgetLayout = widgetLayout
    widget.colors = undefined
    widget.settings = undefined
    return widget
}

function fillTemplateSettings (widget: WidgetList) {
    if (!widget) {
        return
    }
    const TemplateID = widget.TemplateID
    const template = getWidgetTemplate(TemplateID)
    if (!template) {
        return
    }

    const { TemplateSettings } = template

    return { ...TemplateSettings }
}

export function getWidgetLayoutDefaultColors (widget: WidgetList | IWidgetTemplateListItem): Record<string, string> | undefined {
    switch (true) {
        case isQueueGauge(widget):
            return { ...defaultQueueGaugeChartColors() }
        case isMultiQueuesDashboard(widget):
            return { ...defaultMultiQueuesDashboardColors() }
        case isAreaChartWidget(widget):
            return { ...defaultAreaChartColors() }
        case isQueueChart(widget):
            return { ...defaultQueueChartColors() }
        case isWidgetCard(widget):
            return { ...getAllDefaultCardColors() }
        default:
            return { ...getAllDefaultCardColors() }
    }
}

export function getWidgetLayoutColors (widget: WidgetList | IWidgetTemplateListItem) {
    const baseColors = getWidgetLayoutDefaultColors(widget)
    const colors = widget.WidgetLayout?.colors || {}
    return {
        ...baseColors,
        ...colors
    }
}

export function getTemplateParameterPrettyName (parameter: WidgetConfig) {
    if (parameter.ParameterPrettyNameContentTag && i18n.te(parameter.ParameterPrettyNameContentTag)) {
        return i18n.t(parameter.ParameterPrettyNameContentTag)
    }

    return parameter.ParameterPrettyName
}

export function getTemplateParameterHelpName (parameter: WidgetConfig) {
    if (parameter.ParameterTitleTag && i18n.te(parameter.ParameterTitleTag)) {
        return i18n.t(parameter.ParameterTitleTag)
    }

    return getTemplateParameterPrettyName(parameter)
}

export function getTemplateParameterHelpDescription (parameter: WidgetConfig) {
    if (parameter.ParameterDescriptionTag && i18n.te(parameter.ParameterDescriptionTag)) {
        return i18n.t(parameter.ParameterDescriptionTag) as string
    }

    return parameter.ParameterDescription
}

export function styleForStatusIconColor (option: ObjectAnyType) {
    const statusActive = option.value
    const dynamicColor = selectedAccountData.value?.AccountBreaks?.find((el: {
        StatusID: number,
        ColorCode: string
    }) => Number(el.StatusID) === Number(statusActive))
    const color = dynamicColor && dynamicColor.ColorCode
        ? dynamicColor.ColorCode
        : redesignStatusTypesWithCounterWidgets.value?.[statusActive]?.color

    return {
        '--status-svg-color': color
    }
}

export function getWidgetStatusType (status?: number) {
    if (!status) {
        return {
            icon: '',
            text: ''
        }
    }
    return redesignStatusTypesWithCounterWidgets.value[status]
}

export function getWidgetStatusList () {
    const finalStatuses = getStoreStatuses()
    const _statusTypes = getStatusTypes()
    const statusesToAdd = [ callStatuses.IN_CALL, callStatuses.HOLD, otherStatuses.AT_WORK ]

    statusesToAdd.forEach(status => {
        finalStatuses.push({
            ..._statusTypes[status],
            text: i18n.t(_statusTypes[status].text) as string
        })
    })

    const counterWidgetTypes = redesignStatusTypesWithCounterWidgets.value
    const counterOnlyKeys = Object.keys(counterWidgetTypes)
        .map(Number)
        .filter(key => !_statusTypes[key])

    counterOnlyKeys.forEach(key => {
        const status = counterWidgetTypes[key]
        finalStatuses.push({
            ...status,
            text: i18n.t(status.text) as string
        })
    })

    return finalStatuses
}

export function isOtherFilters (ParameterType: number) {
    return ![
        ...ENUM.AUTO_COMPLETE_TYPE_KEYS,
        ...ENUM.MULTISELECT_TYPE_KEYS,
        ...ENUM.MULTISELECT_EXTENDED
    ].includes(ParameterType)
}

export function getWidgetConfigMapped (widgetConfig: Array<WidgetConfig>) {
    if (!widgetConfig || !widgetConfig.length) {
        return {
            filters: [],
            otherFilters: []
        }
    }
    return widgetConfig.reduce((acc, filter) => {
        if (isOtherFilters(filter.ParameterType || -1) && filter.ParameterType) {
            acc.otherFilters.push(filter)
        } else if (!isOtherFilters(filter.ParameterType || -1)) {
            acc.filters.push(filter)
        }
        return acc
    }, {
        filters: [],
        otherFilters: []
    } as { filters: Array<WidgetConfig>, otherFilters: Array<WidgetConfig> })
}

export function isAutoComplete (config: Partial<WidgetConfig>): boolean {
    return ENUM.AUTO_COMPLETE_TYPE_KEYS.includes(config.ParameterType || -1) && !ENUM.NOT_TREE_PARAMETER_NAMES.includes(config.ParameterName || '')
}

export function isAutoCompleteSelect (config: Partial<WidgetConfig>): boolean {
    return ENUM.AUTO_COMPLETE_TYPE_KEYS.includes(config.ParameterType || -1) && ENUM.NOT_TREE_PARAMETER_NAMES.includes(config.ParameterName || '')
}
export function isSelectExtended (config: Partial<WidgetConfig>) {
    return ENUM.MULTISELECT_EXTENDED.includes(config.ParameterType || -1)
}

export function isMultiSelect (config: WidgetConfig) {
    return ENUM.MULTISELECT_TYPE_KEYS.includes(config.ParameterType || -1)
}

export function templateDataTypesKey (dataTypes?: Array<string>): string {
    return [ ...(dataTypes ?? []) ].sort().join(',')
}

export function withTemplateDataTypes (
    config: WidgetConfig,
    dataTypes?: Array<string>
): WidgetConfig {
    if (!isSelectExtended(config)) {
        return config
    }

    return {
        ...config,
        allowedDataTypes: dataTypes ?? []
    }
}

export function uniqueConfigKey (config: WidgetConfig): string {
    return [
        config.ParameterName ?? '',
        config.ParameterType ?? '',
        templateDataTypesKey(config.allowedDataTypes)
    ].join('|')
}

export function stripLocalConfigFields (config: WidgetConfig): WidgetConfig {
    const stripped = { ...config }
    delete stripped.allowedDataTypes
    delete stripped.widgetTitles
    return stripped
}

export function getWidgetConfigByTemplateId (config: WidgetConfig, templateId: number) {
    const template = getWidgetTemplate(templateId)

    return {
        ...getWidgetParameterConfig(template, config.ParameterID, [ 'EntityTypeID', 'ParameterData', 'ParameterDescriptionTag', 'ParameterTitleTag', 'ParameterPrettyNameContentTag', 'ParameterPrettyName' ]),
        ...config
    }
}

function getStoreStatuses (): Array<IAccountStatusItem> {
    const storeStatuses = selectedAccountData.value?.AccountBreaks as Array<IAccountStatusItem>
    const statusTypes = getStatusTypes()
    const localStatuses = Object.values(statusTypes)
    if (storeStatuses?.length) {
        return storeStatuses.map((status) => {
            const otherData: ObjectAnyType = localStatuses.find(s => s.value === status.StatusID) || {}
            if (otherData) {
                otherData['text'] = getSelectedAccountStatusTextById(otherData.value)
            }
            return {
                ...status,
                ...otherData,
            }
        })
    } else {
        return localStatuses.map(status => {
            const statusText = getSelectedAccountStatusTextById(status.value)
            return {
                ...status,
                text: statusText,
            }
        })
    }

}

export function removeWidgetParameterValue (config: WidgetConfig) {
    if (Number(config.WidgetParameterJson) === 1) {
        delete config.WidgetParameterValue
    } else {
        delete config.WidgetParameterValueJson
    }
    return config
}

export function filteredDefaultDataParams (widget: WidgetList) {
    // Remove Default Colors
    const layout = removeTemplateDefaultColors(widget.WidgetLayout)

    return {
        ...widget,
        WidgetLayout: layout
    }
}

export function removeTemplateDefaultColors (widgetLayout: WidgetLayout) {
    const colors = widgetLayout.colors
    if (!colors) {
        return widgetLayout
    }
    const finalColors = removeDefaultColors(colors)
    return {
        ...widgetLayout,
        colors: finalColors
    }
}

function removeDefaultColors (colors: Colors) {
    const defaultColors: Colors = getAllDefaultCardColors()
    const keysToRemove = Object.keys(defaultColors) as Array<keyof Colors>
    keysToRemove.filter((key) => colors[key] === defaultColors[key]).forEach((key) => delete colors[key])
    return colors
}
